import React, { useState, useEffect } from 'react';
import { X, Package, Calendar, Sparkles } from 'lucide-react';
import { API_CONFIG } from './config/apiConfig';

const CHANGE_TYPES = {
    new:         { label: 'Nouveau',      color: '#10b981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.3)'  },
    fix:         { label: 'Correctif',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
    improvement: { label: 'Amélioration', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.3)'  },
    other:       { label: 'Autre',        color: '#E8B75A', bg: 'rgba(232,183,90,0.12)',  border: 'rgba(232,183,90,0.3)'  },
};

const normalizeChange = (c) => typeof c === 'string' ? { type: 'other', text: c } : c;

const ChangelogModal = ({ user, onClose }) => {
    const [changelog, setChangelog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    useEffect(() => {
        if (user?.id) loadChangelog();
    }, [user?.id]);

    const loadChangelog = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/changelogs/check?user_id=${user.id}`, {
                headers: { 'X-Admin-Token': localStorage.getItem('cinestream_token') || '' }
            });
            const data = await response.json();
            if (data.success && data.has_unseen && data.latest_changelog) {
                setChangelog(data.latest_changelog);
            } else {
                onClose();
            }
        } catch (error) {
            console.error('Erreur chargement changelog:', error);
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!changelog) return;
        try {
            await fetch(`${API_CONFIG.NEW_API_BASE}/changelogs/mark-seen`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': localStorage.getItem('cinestream_token') || ''
                },
                body: JSON.stringify({ user_id: user.id, version: changelog.version })
            });
        } catch (error) {
            console.error('Erreur marquage vu:', error);
        } finally {
            onClose();
        }
    };

    const overlayStyle = {
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(12px)',
        zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
    };

    if (loading) {
        return (
            <div style={overlayStyle}>
                <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: '3px solid var(--glass-border)',
                    borderTopColor: 'var(--accent)',
                    animation: 'spinSlow 0.8s linear infinite',
                }} />
            </div>
        );
    }

    if (!changelog) return null;

    const changes = changelog.changes.map(normalizeChange);

    return (
        <div style={overlayStyle}>
            <div style={{
                background: 'var(--bg-modal)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-2xl)',
                boxShadow: 'var(--shadow-xl)',
                width: '100%', maxWidth: 640,
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                animation: 'scaleIn 0.25s ease-out',
            }}>
                {/* Header */}
                <div style={{
                    position: 'relative',
                    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
                    padding: '28px 24px 24px',
                    overflow: 'hidden',
                }}>
                    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.2, pointerEvents: 'none' }}>
                        {[...Array(12)].map((_, i) => (
                            <Sparkles
                                key={i}
                                style={{
                                    position: 'absolute', color: '#fff',
                                    width: 10 + (i % 3) * 6, height: 10 + (i % 3) * 6,
                                    left: `${(i * 8.3) % 100}%`, top: `${(i * 13.7) % 100}%`,
                                    animation: `glowPulse ${1.5 + (i % 3) * 0.4}s infinite`,
                                    animationDelay: `${i * 0.15}s`,
                                }}
                            />
                        ))}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{
                                    width: 52, height: 52, borderRadius: 'var(--r-lg)',
                                    background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Package style={{ width: 26, height: 26, color: '#fff' }} />
                                </div>
                                <div>
                                    <h2 style={{ color: '#fff', fontWeight: 800, fontSize: '1.625rem', lineHeight: 1.2 }}>
                                        Nouvelle version !
                                    </h2>
                                    <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', marginTop: 2 }}>
                                        Pop Movie
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleConfirm}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36, borderRadius: 'var(--r-md)',
                                    background: 'rgba(255,255,255,0.15)', border: 'none',
                                    color: 'rgba(255,255,255,0.8)', cursor: 'pointer',
                                    transition: 'background 150ms ease, color 150ms ease',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
                            >
                                <X style={{ width: 18, height: 18 }} />
                            </button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,0.9)' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '5px 14px', borderRadius: 'var(--r-full)',
                                background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                                fontWeight: 700, fontSize: '1rem',
                            }}>
                                <Package style={{ width: 16, height: 16 }} />
                                v{changelog.version}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.875rem' }}>
                                <Calendar style={{ width: 14, height: 14 }} />
                                {new Date(changelog.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    <h3 style={{ color: 'var(--text-1)', fontWeight: 800, fontSize: '1.25rem', marginBottom: 16 }}>
                        {changelog.title}
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {changes.map((c, index) => {
                            const typeInfo = CHANGE_TYPES[c.type] || CHANGE_TYPES.other;
                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex', alignItems: 'flex-start', gap: 10,
                                        background: 'var(--glass)',
                                        border: '1px solid var(--glass-border)',
                                        borderRadius: 'var(--r-lg)',
                                        padding: '12px 14px',
                                        transition: 'background 150ms ease, border-color 150ms ease',
                                        animation: 'fadeUp 0.3s ease-out',
                                        animationDelay: `${index * 0.06}s`,
                                        animationFillMode: 'both',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-hover)'; e.currentTarget.style.borderColor = 'var(--glass-border-hover)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass)'; e.currentTarget.style.borderColor = 'var(--glass-border)'; }}
                                >
                                    <span style={{
                                        padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                                        color: typeInfo.color, background: typeInfo.bg,
                                        border: `1px solid ${typeInfo.border}`,
                                        flexShrink: 0, marginTop: 2, whiteSpace: 'nowrap',
                                    }}>
                                        {typeInfo.label}
                                    </span>
                                    <p style={{ color: 'var(--text-2)', lineHeight: 1.6, fontSize: '0.9375rem' }}>{c.text}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{
                        marginTop: 20,
                        background: 'linear-gradient(135deg, rgba(232,183,90,0.08), rgba(196,70,47,0.06))',
                        border: '1px solid rgba(232,183,90,0.2)',
                        borderRadius: 'var(--r-lg)',
                        padding: '14px 16px',
                        textAlign: 'center',
                    }}>
                        <p style={{ color: 'var(--text-3)', fontSize: '0.875rem' }}>
                            ✨ Merci d'utiliser Pop Movie ! Profitez de ces nouvelles fonctionnalités.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid var(--glass-border)', background: 'var(--glass)', flexShrink: 0 }}>
                    <button
                        onClick={handleConfirm}
                        className="btn-primary"
                        style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 700 }}
                    >
                        J'ai compris, c'est parti ! 🚀
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangelogModal;
